/**
 * WithVacationIndicator Component
 *
 * A wrapper component that adds a vacation indicator to location rows.
 * Similar to WithDeactivatedIndicator but specifically for vacation holds.
 *
 * Features:
 * - Shows a purple vacation indicator bar on the left side when showIndicator is true
 * - Displays a vacation icon avatar in the top-left corner
 * - Uses vacation hold colors from the theme (purple)
 * - Matches the design pattern of the deactivated indicator
 *
 * @param {boolean} showIndicator - Whether to show the vacation indicator
 * @param {ReactNode} children - Child components to render inside the indicator
 * @param {ReactNode} Icon - Custom icon component (defaults to VacationIcon)
 * @param {object} ...rest - Additional props passed to the container
 */
import React from 'react'
import PropTypes from 'prop-types'
import { Box, SvgIcon, useTheme } from '@mui/material'
import { ReactComponent as VacationIcon } from 'assets/services/hold/VacationIcon.svg'
import Avatar from '@mui/material/Avatar'
import { getLocationRowBgcolor } from 'components/locations/settings'
import { get } from 'utils/lodash'

const WithVacationIndicator = ({ showIndicator = false, children, Icon = <VacationIcon />, ...rest }) => {
  const theme = useTheme()
  const bgcolor = getLocationRowBgcolor(rest)
  const maxHeight = get(rest, 'rowHeight', '100%')
  const containerStyle = get(rest, 'containerStyle', {})

  return (
    <Box
      display="flex"
      flexDirection="row"
      sx={{
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.divider,
        borderBottomColor: theme.palette.divider,
        borderBottomStyle: 'solid',
        borderBottomWidth: 1,
        borderLeft: 0,
        borderRight: 0,
        width: '100%',
        maxHeight,
        ...containerStyle,
      }}
      {...rest}
    >
      {showIndicator && (
        <Box
          className="vacation-bg-indicator"
          sx={{
            width: theme.spacing(1),
            bgcolor: showIndicator ? theme.palette.background.hold : bgcolor,
          }}
        />
      )}
      <Box
        sx={{
          borderLeftStyle: showIndicator && 'solid',
          borderLeftWidth: 2,
          borderLeftColor: showIndicator ? theme.palette.border.hold : 'inherit',
          position: 'relative',
          flex: 1,
          ...(!showIndicator && {
            '& .MuiDataGrid-row, & .expand-toggle-location-group-details': {
              pl: 1.2,
            },
          }),
        }}
      >
        {children}
        {showIndicator && (
          <Avatar
            sx={{
              position: 'absolute',
              width: 18,
              height: 18,
              bgcolor: theme.palette.border.hold,
              top: 4,
              left: -6,
            }}
          >
            <SvgIcon
              sx={{
                fontSize: 12,
              }}
            >
              {Icon}
            </SvgIcon>
          </Avatar>
        )}
      </Box>
    </Box>
  )
}

WithVacationIndicator.propTypes = {
  showIndicator: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
  Icon: PropTypes.node,
}

export default WithVacationIndicator
